import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { endpoints, dollars } from './api';
import { useSession, useWindowContext } from './session';
import { RegisterGate } from './pages/Register';
import { PLATFORM_NAME } from '@bre-ticketing/contracts';

/**
 * The window shell: a status bar (who, which register, drawer), three
 * tabs (Sell, Orders, Drawer) and the content. Until the register is open
 * the Register gate takes over the whole screen, so nothing sells before a
 * session exists (docs/auth.md §3.3).
 */

const TABS = [
  { to: '/sell', label: 'Sell' },
  { to: '/orders', label: 'Orders & will call' },
  { to: '/drawer', label: 'Drawer' },
];

export default function App() {
  const session = useSession();
  const context = useWindowContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useMutation({
    mutationFn: endpoints.logout,
    onSettled: async () => {
      queryClient.clear();
      await navigate('/login', { replace: true });
    },
  });

  const ctx = context.data;
  return (
    <div className="flex min-h-full flex-col">
      <header className="flex flex-wrap items-center gap-x-6 gap-y-2 bg-shell-900 px-4 py-2 text-sm text-white">
        <span>
          <span className="font-display font-bold tracking-tight">{PLATFORM_NAME}</span> <span className="text-gold">Box Office</span>
        </span>
        <span className="text-white/70">{ctx?.register ? `${ctx.register.name} · ${ctx.venue?.name ?? ''}` : (ctx?.device.name ?? '')}</span>
        <span className="text-white/70">{session.data?.staff.display_name}</span>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${ctx?.session ? 'bg-ok text-white' : 'bg-shell-700 text-white/70'}`}>{ctx?.session ? 'Register open' : 'Register closed'}</span>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${ctx?.shift ? 'bg-ok text-white' : 'bg-shell-700 text-white/70'}`}>{ctx?.shift ? `Drawer open · float ${dollars(ctx.shift.opening_float_cents)}` : 'No drawer'}</span>
        <nav className="ml-auto flex gap-1">
          {TABS.map((tab) => (
            <NavLink key={tab.to} to={tab.to} className={({ isActive }) => `touch flex items-center rounded px-4 text-sm font-medium ${isActive ? 'bg-shell-700 text-white' : 'text-white/70 hover:bg-shell-800 hover:text-white'}`}>
              {tab.label}
            </NavLink>
          ))}
        </nav>
        <button type="button" onClick={() => signOut.mutate()} className="touch rounded px-3 text-sm text-white/70 hover:text-white">
          Sign out
        </button>
      </header>
      <main className="flex-1 p-4">{ctx && !ctx.session ? <RegisterGate context={ctx} /> : <Outlet />}</main>
    </div>
  );
}
